import { getCategories, getChildCategoriesByParentId } from 'app-components/flux/categories/categoriesSelectors';
import { TAX_LINE_ITEMS_REDUCER_KEY, taxLineItemsSelectors } from 'app-components/flux/tax-line-items';
import { ValidationTrigger, createManagerFactory, formBuilder } from 'app-components/utils/createForm/formBuilder';

import {
  CATEGORIES_REDUCER_KEY,
  CategoryProps,
  CategoryType,
  CategoryTypeEnum,
  SubmitPayload,
  TaxGroupType,
  UsageTypeEnum,
} from './categoriesTypes';

import type RootState from 'app-components/utils/redux-store/rootState';
import { createSelector } from 'reselect';
import { taxGroupNames } from '../business/businessTypes';

type ReduxState = Pick<RootState, CATEGORIES_REDUCER_KEY | typeof TAX_LINE_ITEMS_REDUCER_KEY>;

export const clearState: CategoryProps = {
  id: undefined,
  clientId: undefined,
  parentId: undefined,
  parentClientId: undefined,
  type: undefined,
  categoryType: undefined,
  name: undefined,
  description: undefined,

  usageType: undefined,

  isNotEditable: undefined,
  isNotUserAssignable: undefined,

  isExcludedFromBudgets: undefined,
  isExcludedFromCategoryList: undefined,
  isExcludedFromReports: undefined,

  knownCategoryId: undefined,
  isInvestment: undefined,

  logoUrl: undefined,
  txfId: undefined,
  txfIds: undefined,

  isDeleted: undefined,
  modifiedAt: undefined,
};

const selectHasDuplicateSibling = createSelector(
  getCategories,
  (_state: unknown, { parentId }: CategoryProps) => parentId,
  (_state: unknown, { id }: CategoryProps) => id,
  (_state: unknown, { usageType }: CategoryProps) => usageType,
  (_state: unknown, { name }: CategoryProps) => name?.toLowerCase().trim(),
  (categories, parentId, id, usageType, lowerTrimmedName) =>
    categories.some(
      (category) =>
        category.parentId === parentId &&
        category.id !== id &&
        category.usageType === usageType &&
        category.name?.toLowerCase().trim() === lowerTrimmedName,
    ),
);

const selectParent = createSelector(
  getCategories,
  (_state: unknown, parentId: string) => parentId,
  (categories, parentId) => categories.find((category) => category.id === parentId),
);

const selectHasSameParentName = createSelector(
  getCategories,
  (_state: unknown, { parentId }: CategoryProps) => parentId,
  (_state: unknown, { name }: CategoryProps) => name?.toLowerCase().trim(),
  (categories, parentId, lowerTrimmedName) =>
    categories.find((category) => category.id === parentId && category.name?.toLowerCase().trim() === lowerTrimmedName),
);

const selectIsAChild = createSelector(
  (state: ReduxState, { id }: CategoryProps) => getChildCategoriesByParentId(state, id ?? ''),
  (_state: unknown, { parentId }: CategoryProps) => parentId,
  (childCategories, parentId) => childCategories.find((category) => category.id === parentId),
);

const selectNameExists = createSelector(
  getCategories,
  (_state: unknown, { id }: CategoryProps) => id,
  (_state: unknown, { parentId }: CategoryProps) => parentId || '0',
  (_state: unknown, { name }: CategoryProps) => name?.toLowerCase().trim(),
  (_state: unknown, { usageType }: CategoryProps) => usageType,
  (categories, id, parentId, lowerTrimmedName, usageType) =>
    !id &&
    categories.find(
      (category) =>
        category.name?.toLowerCase().trim() === lowerTrimmedName &&
        category.usageType === usageType &&
        category.parentId === parentId,
    ),
);

export const areTxfIdsSet = (txfIds: Record<string, string> | undefined | null): boolean => {
  if (!txfIds) {
    return false;
  }

  if (Object.keys(txfIds).length === 0) {
    return false;
  }

  if (Object.values(txfIds).every((value) => value === '')) {
    return false;
  }

  return true;
};

export const disabledIfNotEditable = (state: { initialInput: Partial<CategoryProps> }) =>
  !!state.initialInput.isNotEditable;

const init = ({ input, store }: { input: Partial<CategoryProps>; store: ReduxState }) => {
  const taxLineItemsById = taxLineItemsSelectors.getResourcesById(store);
  const initialPersonalTaxForm = input?.txfId && taxLineItemsById?.get(input.txfId)?.formId;

  const initialBusinessInfo: Record<string, any> = {};

  Object.entries(taxGroupNames).forEach(([key]) => {
    initialBusinessInfo[key] = {
      taxForm: undefined,
      taxLineItem: undefined,
      enabled: false,
    };
  });

  if (input.txfIds) {
    Object.entries(input.txfIds).forEach(([key, value]) => {
      initialBusinessInfo[key] = {
        taxForm: value ? taxLineItemsById?.get(value)?.formId : value,
        taxLineItem: value,
        enabled: !!value,
      };
    });
  }

  const initialValue = {
    ...clearState,
    ...input,
    initialInput: input,
    type: input.type || CategoryTypeEnum.EXPENSE,
    usageType: input.usageType || UsageTypeEnum.PERSONAL,
    // these are meta fields for the Personal case
    taxRelated: !!input.txfId || areTxfIdsSet(input.txfIds),
    taxForm: initialPersonalTaxForm,
    businessTaxInfo: initialBusinessInfo,
  };

  return initialValue;
};

const empty: Record<string, string> = {};

type State = { taxRelated?: boolean } & ReturnType<typeof init>;

export const selectInitialValues = ({ state }: { state: State }) => state;

const categoryFormBuilder = formBuilder({ init })
  .addField('id', {
    selector: (state) => state.id || undefined,
    onValueChange: (draft, value: string | undefined) => {
      draft.name = value;
    },
  })
  .addField('name', {
    selector: (state) => state.name || '',
    onValueChange: (draft, value: string) => {
      draft.name = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state, store) =>
          !!state.name &&
          !selectHasDuplicateSibling(store, state) &&
          !selectHasSameParentName(store, state) &&
          !selectNameExists(store, state),
        error: (state, store) => {
          if (!state.name) {
            return 'Name is required';
          }
          if (selectNameExists(store, state)) {
            return 'Name already exists';
          }
          if (selectHasDuplicateSibling(store, state)) {
            return 'Name cannot be same as another category with same parent';
          }
          if (selectHasSameParentName(store, state)) {
            return 'Category and subcategory names must be different';
          }
          return '';
        },
      },
    },
    disabledSelector: disabledIfNotEditable,
  })
  .addField('parent', {
    selector: (state) => state.parentId || '0',
    onValueChange: (draft, value: string, store) => {
      draft.parentId = value;

      const parent = selectParent(store, value);
      if (parent) {
        parent.type && (draft.type = parent.type);
        parent.usageType && (draft.usageType = parent.usageType);
      }
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onChange,
        isValid: (state, store) => !selectIsAChild(store, state),
        error:
          'Invalid parent-child relationship. A category cannot be both a parent and a child of another category within the same hierarchy.',
      },
    },
    disabledSelector: disabledIfNotEditable,
  })
  .addField('type', {
    selector: (state) => state.type || CategoryTypeEnum.EXPENSE,
    onValueChange: (draft, value: CategoryType) => {
      draft.type = value;
    },
    disabledSelector: disabledIfNotEditable,
  })
  .addField('usageType', {
    selector: (state) => state.usageType || (UsageTypeEnum.PERSONAL as string),
    onValueChange: (draft, value: string) => {
      draft.usageType = value;
    },
    disabledSelector: disabledIfNotEditable,
  })

  // for usageType === PERSONAL
  .addField('txfId', {
    selector: (state) => state.txfId,
    onValueChange: (draft, value: string | undefined) => {
      draft.txfId = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onBlur,
        isValid: (state) =>
          state.usageType === UsageTypeEnum.BUSINESS ? true : !state.taxRelated || (state.taxRelated && !!state.txfId),
        error: 'Required',
      },
    },
  })

  // for usageType === BUSINESS
  .addField('txfIds', {
    selector: (state) => state.txfIds || empty,
    onValueChange: (_draft, _value: Record<string, string>) => {},
    validation: {
      required: {
        triggers: ValidationTrigger.onBlur,
        isValid: (state) => {
          if (state.usageType === UsageTypeEnum.PERSONAL || !state.txfIds || Object.keys(state.txfIds).length === 0) {
            return true;
          }

          if (Object.values(state.txfIds).every((value) => !!value || value === '')) {
            return true;
          }
          return false;
        },
        error: 'Required',
      },
    },
  })
  .addField('description', {
    selector: (state) => state.description || 'Select a tax line to see a description',
    onValueChange: (draft, value: string) => {
      draft.description = value;
    },
  })

  // ================= Extra Props - START =================
  // NOTE:  These are extra properties, not part of the QCS schema, required to manage the UI form state

  // for usageType === PERSONAL
  .addField('taxRelated', {
    selector: (state) => !!state.taxRelated || !!state.txfId,
    onValueChange: (draft, value: boolean) => {
      draft.taxRelated = value;
      draft.txfId = '';
      draft.taxForm = '';
    },
  })

  // for usageType === PERSONAL
  .addField('taxForm', {
    selector: (state) => state.taxForm,
    onValueChange: (draft, value: string | undefined) => {
      draft.taxForm = value;
      draft.txfId = undefined;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onBlur,
        isValid: (state) =>
          state.usageType === UsageTypeEnum.BUSINESS
            ? true
            : !state.taxRelated || (state.taxRelated && !!state.taxForm),
        error: 'Required',
      },
    },
  })

  // for usageType === BUSINESS
  .addField('taxGroups', {
    selector: (state) => state.taxGroups || {},
    onValueChange: (draft, value: TaxGroupType) => {
      draft.taxGroups = value;
    },
  })
  // for usageType === BUSINESS
  .addMultiField('businessTaxInfo', {
    selector: (state, key: string) => state.businessTaxInfo[key],
    onValueChange: (
      draft,
      key: string,
      value: { name: 'enabled'; value: boolean } | { name: 'taxForm' | 'taxLineItem'; value: string },
    ) => {
      //update the metadata field
      draft.businessTaxInfo[key].enabled = !!value;
      draft.businessTaxInfo[key][value.name] = value.value;

      //update the actual field
      draft.txfIds = !draft.txfIds ? empty : draft.txfIds;
      if (draft.businessTaxInfo[key].enabled === false) {
        draft.txfIds[key] = '';
        draft.businessTaxInfo[key].taxForm = undefined;
        draft.businessTaxInfo[key].taxLineItem = undefined;
      } else {
        const lineItemValue = value.name === 'taxLineItem' ? value.value : '';

        if (value.name === 'taxForm') {
          // Reset the line item if the tax form changes
          draft.businessTaxInfo[key].taxLineItem = undefined;
        }

        if (draft.txfIds[key] === undefined) {
          draft.txfIds = { ...draft.txfIds, [key]: lineItemValue };
        } else {
          draft.txfIds[key] = lineItemValue;
        }
      }
    },
    selectActiveKeys: (state) => Object.keys(state.businessTaxInfo),
    validation: {
      required: {
        triggers: ValidationTrigger.onBlur,
        isValid: (state, key) => {
          if (state.usageType === UsageTypeEnum.PERSONAL) {
            return true;
          }

          return state.businessTaxInfo[key].enabled ? !!state.txfIds?.[key] && state.txfIds?.[key] !== '' : true;
        },
        error: 'Required',
      },
    },
  })
  //================= Extra Props - END =================

  .build({
    submitTransform(state): SubmitPayload {
      return { category: state };
    },
  });

export const categoryFormFactory = createManagerFactory(categoryFormBuilder);
